import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  ArrowRight,
  Clock,
  TrendingUp,
  FileText,
  Download
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { auth } from '../../firebase/config';
import { updateUserProfile, getUserProfile } from '../../firebase/collections';
import SmartPaymentModal from '../../components/payment/SmartPaymentModal';
import SubscriptionPaymentModal from '../../components/payment/SubscriptionPaymentModal';
import subscriptionService from '../../services/subscriptionService';
import { paymentManagementService } from '../../firebase/paymentManagement';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import toast from 'react-hot-toast';
import { getSubscriptionPlans, CATEGORIES, STUDENT_RANGES } from '../../utils/subscriptionPricing';

const SubscriptionManagement = () => {
  const { currentUser, userProfile, refreshUserProfile } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [actionType, setActionType] = useState(null); // 'renew', 'upgrade', 'change'
  const [enableAutopay, setEnableAutopay] = useState(false);
  const [cancellingAutopay, setCancellingAutopay] = useState(false);
  
  // For renewal with plan changes
  const [selectedCategory, setSelectedCategory] = useState(userProfile?.category || 'coaching');
  const [selectedStudentRange, setSelectedStudentRange] = useState(userProfile?.studentRange || '0-500');

  // Get dynamic subscription plans based on selected category and student range
  const subscriptionPlans = selectedCategory && selectedStudentRange
    ? getSubscriptionPlans(selectedCategory, selectedStudentRange)
    : getSubscriptionPlans('coaching', '0-500'); // Default fallback

  useEffect(() => {
    loadPaymentHistory();
  }, [currentUser]);

  // Initialize selected category and student range from user profile
  useEffect(() => {
    if (userProfile?.category) {
      setSelectedCategory(userProfile.category);
    }
    if (userProfile?.studentRange) {
      setSelectedStudentRange(userProfile.studentRange);
    }
  }, [userProfile]);

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      // Get coaching subscription payments
      let q = query(
        collection(db, COLLECTIONS.COACHING_SUBSCRIPTION_PAYMENTS),
        where('userId', '==', currentUser.uid),
        orderBy('paymentDate', 'desc'),
        limit(20)
      );

      let querySnapshot;
      let needsClientSideSort = false;
      try {
        querySnapshot = await getDocs(q);
      } catch (queryError) {
        // Handle missing Firestore index - fallback to query without orderBy
        if (queryError.code === 'failed-precondition' || queryError.message?.includes('index')) {
          needsClientSideSort = true;
          q = query(
            collection(db, COLLECTIONS.COACHING_SUBSCRIPTION_PAYMENTS),
            where('userId', '==', currentUser.uid),
            limit(50) // Get more to sort client-side
          );
          querySnapshot = await getDocs(q);
        } else {
          throw queryError;
        }
      }

      const payments = [];
      querySnapshot.forEach((doc) => {
        payments.push({ id: doc.id, ...doc.data() });
      });

      // Sort by paymentDate if we used fallback query (client-side sorting)
      if (needsClientSideSort && payments.length > 0) {
        payments.sort((a, b) => {
          const dateA = a.paymentDate?.toDate 
            ? a.paymentDate.toDate() 
            : (a.paymentDate ? new Date(a.paymentDate) : new Date(0));
          const dateB = b.paymentDate?.toDate 
            ? b.paymentDate.toDate() 
            : (b.paymentDate ? new Date(b.paymentDate) : new Date(0));
          return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
        });
      }

      // Limit to 20 after sorting
      setPaymentHistory(payments.slice(0, 20));
    } catch (error) {
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        toast.error('Payment history index is being created. Please refresh in a moment.');
      } else {
        toast.error('Failed to load payment history');
      }
      setPaymentHistory([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPlan = () => {
    if (!userProfile?.subscription) return null;
    return subscriptionPlans.find(p => p.id === userProfile.subscription.plan);
  };

  const getSubscriptionStatus = () => {
    if (!userProfile?.subscription) return 'inactive';
    return userProfile.subscription.status || 'inactive';
  };

  const isSubscriptionActive = () => {
    const status = getSubscriptionStatus();
    if (status === 'active') {
      // Check if subscription has expired
      if (userProfile.subscription.endDate) {
        const endDate = userProfile.subscription.endDate?.toDate 
          ? userProfile.subscription.endDate.toDate() 
          : new Date(userProfile.subscription.endDate);
        return endDate > new Date();
      }
      return true;
    }
    return false;
  };

  const getDaysRemaining = () => {
    if (!userProfile?.subscription?.endDate) return 0;
    const endDate = userProfile.subscription.endDate?.toDate 
      ? userProfile.subscription.endDate.toDate() 
      : new Date(userProfile.subscription.endDate);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleRenew = (plan) => {
    setSelectedPlan(plan);
    setActionType('renew');
    if (enableAutopay) {
      setShowSubscriptionModal(true);
    } else {
      setShowPaymentModal(true);
    }
  };

  const handleChangePlan = (plan) => {
    setSelectedPlan(plan);
    setActionType('change');
    if (enableAutopay) {
      setShowSubscriptionModal(true);
    } else {
      setShowPaymentModal(true);
    }
  };

  const handleCancelAutopay = async () => {
    if (!userProfile?.subscription?.razorpaySubscriptionId) {
      toast.error('No active autopay subscription found');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel autopay? Your subscription will remain active until the current billing period ends.')) {
      return;
    }

    setCancellingAutopay(true);
    try {
      const result = await subscriptionService.cancelSubscription(
        userProfile.subscription.razorpaySubscriptionId,
        true // Cancel at cycle end
      );

      if (result.success) {
        await updateUserProfile(currentUser.uid, {
          'subscription.autopayEnabled': false,
          'subscription.autopayCancelledAt': new Date(),
          updatedAt: new Date()
        });

        toast.success('Autopay cancelled. Your subscription will remain active until the current period ends.');
        if (refreshUserProfile) {
          await refreshUserProfile();
        }
      } else {
        throw new Error(result.error || 'Failed to cancel autopay');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to cancel autopay. Please try again.');
    } finally {
      setCancellingAutopay(false);
    }
  };

  const handlePaymentSuccess = async (response, transactionData) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('User not found');
        return;
      }

      // selectedPlan is already the plan object
      const plan = selectedPlan;
      if (!plan || !plan.id) {
        toast.error('Plan not found');
        return;
      }

      // Calculate new end date
      let newEndDate = new Date();
      const currentEndDate = userProfile?.subscription?.endDate 
        ? (userProfile.subscription.endDate?.toDate 
            ? userProfile.subscription.endDate.toDate() 
            : new Date(userProfile.subscription.endDate))
        : new Date();

      if (actionType === 'renew' && isSubscriptionActive()) {
        // Renew: extend from current end date
        newEndDate = new Date(currentEndDate);
        newEndDate.setMonth(newEndDate.getMonth() + plan.duration);
      } else {
        // Change/New: start from now or extend from now if expired
        newEndDate = new Date();
        if (currentEndDate > new Date()) {
          // If still active, extend from current end date
          newEndDate = new Date(currentEndDate);
        }
        newEndDate.setMonth(newEndDate.getMonth() + plan.duration);
      }

      // Update user profile with new category and student range
      await updateUserProfile(user.uid, {
        'subscription.plan': plan.id,
        'subscription.status': 'active',
        'subscription.startDate': new Date(),
        'subscription.endDate': newEndDate,
        'subscription.transactionId': transactionData?.paymentId || transactionData?.transactionId,
        'subscription.orderId': transactionData?.orderId,
        category: selectedCategory, // Update category
        studentRange: selectedStudentRange, // Update student range
        isActive: true,
        updatedAt: new Date()
      });

      // Save payment record
      await paymentManagementService.saveCoachingSubscriptionPayment({
        coachingName: userProfile?.coachingName || 'Unknown',
        plan: plan.id,
        planName: plan.name,
        coachingId: userProfile?.coachingId,
        transactionId: transactionData?.paymentId || transactionData?.transactionId,
        orderId: transactionData?.orderId,
        paymentStatus: 'completed',
        paymentAmount: plan.price,
        paymentDate: new Date().toISOString(),
        userId: user.uid,
        customerEmail: user.email,
        customerName: userProfile?.contactPerson || user.displayName,
        actionType: actionType
      });

      toast.success('Subscription updated successfully!');
      
      // Refresh user profile
      if (refreshUserProfile) {
        await refreshUserProfile();
      }
      
      // Reload payment history
      await loadPaymentHistory();
      
      setShowPaymentModal(false);
      setShowSubscriptionModal(false);
      setSelectedPlan(null);
      setActionType(null);
    } catch (error) {
      toast.error('Subscription updated but payment verification failed. Please contact support.');
    }
  };

  const handleSubscriptionSuccess = async (response, transactionData) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('User not found');
        return;
      }

      const plan = selectedPlan;
      if (!plan || !plan.id) {
        toast.error('Plan not found');
        return;
      }

      // Calculate new end date based on plan
      let newEndDate = new Date();
      if (plan.id === 'monthly') {
        newEndDate.setMonth(newEndDate.getMonth() + 1);
      } else if (plan.id === 'quarterly') {
        newEndDate.setMonth(newEndDate.getMonth() + 3);
      } else if (plan.id === 'yearly') {
        newEndDate.setFullYear(newEndDate.getFullYear() + 1);
      }

      // Update user profile with autopay subscription and new category/student range
      await updateUserProfile(user.uid, {
        'subscription.plan': plan.id,
        'subscription.status': 'active',
        'subscription.startDate': new Date(),
        'subscription.endDate': newEndDate,
        'subscription.razorpaySubscriptionId': transactionData?.subscriptionId,
        'subscription.transactionId': transactionData?.paymentId || transactionData?.transactionId,
        'subscription.orderId': transactionData?.orderId,
        'subscription.autopayEnabled': true,
        category: selectedCategory, // Update category
        studentRange: selectedStudentRange, // Update student range
        isActive: true,
        updatedAt: new Date()
      });

      // Save payment record
      await paymentManagementService.saveCoachingSubscriptionPayment({
        coachingName: userProfile?.coachingName || 'Unknown',
        plan: plan.id,
        planName: plan.name,
        coachingId: userProfile?.coachingId,
        razorpaySubscriptionId: transactionData?.subscriptionId,
        transactionId: transactionData?.paymentId || transactionData?.transactionId,
        orderId: transactionData?.orderId,
        paymentStatus: 'completed',
        paymentAmount: plan.price,
        paymentDate: new Date().toISOString(),
        userId: user.uid,
        customerEmail: user.email,
        customerName: userProfile?.contactPerson || user.displayName,
        actionType: 'autopay_activation'
      });

      toast.success('Autopay subscription activated successfully!');
      
      // Refresh user profile
      if (refreshUserProfile) {
        await refreshUserProfile();
      }
      
      // Reload payment history
      await loadPaymentHistory();
      
      setShowSubscriptionModal(false);
      setSelectedPlan(null);
      setActionType(null);
    } catch (error) {
      toast.error('Subscription activated but payment verification failed. Please contact support.');
    }
  };

  const handlePaymentFailure = (error) => {
    console.error('Payment/Subscription failure:', error);
    
    // Extract detailed error message
    let errorMessage = 'Payment failed. Please try again.';
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error?.error?.description) {
      errorMessage = error.error.description;
    } else if (error?.error?.reason) {
      errorMessage = error.error.reason;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (error?.description) {
      errorMessage = error.description;
    }
    
    console.error('Error details:', {
      error,
      errorMessage,
      errorType: typeof error,
      errorKeys: error ? Object.keys(error) : []
    });
    
    toast.error(errorMessage || 'Payment failed. Please try again.');
    setShowPaymentModal(false);
    setShowSubscriptionModal(false);
    setSelectedPlan(null);
    setActionType(null);
  };

  const currentPlan = getCurrentPlan();
  const status = getSubscriptionStatus();
  const isActive = isSubscriptionActive();
  const daysRemaining = getDaysRemaining();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Subscription Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage your coaching subscription, view payment history, and renew or change your plan
        </p>
        
        {/* Show category and student range info */}
        {userProfile?.category && userProfile?.studentRange && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Category</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {CATEGORIES.find(c => c.value === userProfile.category)?.label || userProfile.category}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Student Range</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {STUDENT_RANGES.find(r => r.value === userProfile.studentRange)?.label || userProfile.studentRange}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current Subscription Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Current Subscription</h2>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            isActive 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}>
            {isActive ? 'Active' : 'Inactive'}
          </div>
        </div>

        {currentPlan ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <CreditCard className="text-blue-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Plan</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{currentPlan.name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="text-blue-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Valid Until</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(userProfile?.subscription?.endDate)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="text-blue-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Days Remaining</p>
                  <p className={`font-semibold ${daysRemaining < 7 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                    {daysRemaining} days
                  </p>
                </div>
              </div>
            </div>

            {userProfile?.subscription?.transactionId && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Transaction ID: <span className="font-mono">{userProfile.subscription.transactionId}</span>
                </p>
              </div>
            )}

            {/* Autopay Status */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Autopay</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {userProfile?.subscription?.autopayEnabled 
                      ? 'Your subscription will automatically renew'
                      : 'Enable autopay to avoid manual renewals'}
                  </p>
                </div>
                {userProfile?.subscription?.autopayEnabled ? (
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm text-green-600 dark:text-green-400 font-medium">Enabled</span>
                  </div>
                ) : (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableAutopay}
                      onChange={(e) => setEnableAutopay(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                )}
              </div>
              {userProfile?.subscription?.razorpaySubscriptionId && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Subscription ID: <span className="font-mono">{userProfile.subscription.razorpaySubscriptionId}</span>
                </p>
              )}
            </div>

            {!isActive && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5" size={20} />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Your subscription has expired
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      Renew your subscription to continue accessing the coaching admin panel.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isActive && daysRemaining < 30 && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="text-blue-600 dark:text-blue-400 mr-3 mt-0.5" size={20} />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Subscription expiring soon
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      Your subscription will expire in {daysRemaining} days. Renew now to avoid interruption.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <XCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No active subscription</p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              Select a plan below to get started
            </p>
          </div>
        )}
      </motion.div>

      {/* Available Plans */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
      >
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          {currentPlan ? 'Change or Renew Plan' : 'Select a Subscription Plan'}
        </h2>

        {/* Category and Student Range Selectors */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
            Update Your Institute Details (Optional)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Institute Type
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Student Range Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Students
              </label>
              <select
                value={selectedStudentRange}
                onChange={(e) => setSelectedStudentRange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {STUDENT_RANGES.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
            {selectedCategory !== userProfile?.category || selectedStudentRange !== userProfile?.studentRange ? (
              <span className="text-blue-600 dark:text-blue-400 font-medium">
                ℹ️ Pricing updated based on your selection. Your profile will be updated after payment.
              </span>
            ) : (
              <span>
                Current: {CATEGORIES.find(c => c.value === userProfile?.category)?.label} • {STUDENT_RANGES.find(r => r.value === userProfile?.studentRange)?.label}
              </span>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {subscriptionPlans.map((plan) => {
            const isCurrentPlan = currentPlan?.id === plan.id;
            const isUpgrade = currentPlan && 
              (plan.duration > (subscriptionPlans.find(p => p.id === currentPlan.id)?.duration || 0));
            
            return (
              <div
                key={plan.id}
                className={`relative border-2 rounded-lg p-6 ${
                  plan.popular
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-3">
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Current
                    </span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(plan.price)}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">/{plan.period}</span>
                  </div>
                  {plan.savings && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Save {formatCurrency(plan.savings)}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{plan.description}</p>
                </div>
                <div className="mt-6">
                  {isCurrentPlan ? (
                    <button
                      onClick={() => handleRenew(plan)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <RefreshCw size={16} />
                      <span>Renew Plan</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleChangePlan(plan)}
                      disabled={loading}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {isUpgrade ? (
                        <>
                          <TrendingUp size={16} />
                          <span>Upgrade</span>
                        </>
                      ) : (
                        <>
                          <ArrowRight size={16} />
                          <span>Select Plan</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Payment History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Payment History</h2>
          <button
            onClick={loadPaymentHistory}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center space-x-2"
          >
            <RefreshCw size={16} />
            <span>Refresh</span>
          </button>
        </div>

        {paymentHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Plan</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Transaction ID</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr key={payment.id} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                      {payment.planName || payment.plan}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                      {formatCurrency(payment.paymentAmount || payment.amount || 0)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.paymentStatus === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {payment.paymentStatus || 'pending'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 font-mono">
                      {payment.transactionId || payment.id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No payment history found</p>
          </div>
        )}
      </motion.div>

      {/* Payment Modal - One-time payment */}
      {showPaymentModal && selectedPlan && (
        <SmartPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlan(null);
            setActionType(null);
          }}
          paymentData={{
            type: 'coaching_subscription',
            amount: selectedPlan.price,
            currency: 'INR',
            description: `Coaching Subscription - ${selectedPlan.name} (${actionType === 'renew' ? 'Renewal' : 'Change Plan'})`,
            customerName: userProfile?.contactPerson || currentUser?.displayName,
            customerEmail: currentUser?.email,
            customerPhone: userProfile?.phone,
            merchantName: 'Mahstry Private Limited',
            userId: currentUser?.uid,
            metadata: {
              coachingName: userProfile?.coachingName,
              plan: selectedPlan.id,
              planName: selectedPlan.name,
              category: userProfile?.category,
              studentRange: userProfile?.studentRange,
              coachingId: userProfile?.coachingId,
              contactPerson: userProfile?.contactPerson,
              phone: userProfile?.phone,
              actionType: actionType
            }
          }}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          title={`${actionType === 'renew' ? 'Renew' : 'Change'} Subscription - ${selectedPlan.name}`}
        />
      )}

      {/* Subscription Modal - Autopay */}
      {showSubscriptionModal && selectedPlan && (
        <SubscriptionPaymentModal
          isOpen={showSubscriptionModal}
          onClose={() => {
            setShowSubscriptionModal(false);
            setSelectedPlan(null);
            setActionType(null);
          }}
          paymentData={{
            type: 'coaching_subscription',
            amount: selectedPlan.price,
            currency: 'INR',
            description: `Coaching Subscription - ${selectedPlan.name} (${actionType === 'renew' ? 'Renewal' : 'Change Plan'})`,
            customerName: userProfile?.contactPerson || currentUser?.displayName,
            customerEmail: currentUser?.email,
            customerPhone: userProfile?.phone,
            merchantName: 'Mahstry Private Limited',
            userId: currentUser?.uid,
            metadata: {
              coachingName: userProfile?.coachingName,
              plan: selectedPlan.id,
              planName: selectedPlan.name,
              category: userProfile?.category,
              studentRange: userProfile?.studentRange,
              coachingId: userProfile?.coachingId,
              contactPerson: userProfile?.contactPerson,
              phone: userProfile?.phone,
              actionType: actionType
            }
          }}
          onSuccess={handleSubscriptionSuccess}
          onFailure={handlePaymentFailure}
          title={`${actionType === 'renew' ? 'Renew' : 'Change'} Subscription with Autopay - ${selectedPlan.name}`}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;

